import api from '../api/client';
import { EventDetails, UserDetails } from '../types';

// Filter options for browsing. Values must match the backend (/events).
export const SPORTS = ['Triathlon', 'Run', 'Trail Run', 'Swim', 'Cycle', 'Swimrun', 'Duathlon'];
export const STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];
export const DISTANCES: { value: string; label: string }[] = [
  { value: 'tri-sprint', label: 'Triathlon: sprint' },
  { value: 'tri-olympic', label: 'Triathlon: Olympic' },
  { value: 'tri-half', label: 'Triathlon: half / 70.3' },
  { value: 'tri-full', label: 'Triathlon: full / Ironman' },
  { value: 'run-short', label: 'Run: under 10 km' },
  { value: 'run-10k', label: 'Run: 10–15 km' },
  { value: 'run-half', label: 'Run: half marathon (15–30 km)' },
  { value: 'run-marathon', label: 'Run: marathon (30–50 km)' },
  { value: 'run-ultra', label: 'Run: ultra (50 km+)' },
];

// "Within" options (km) for the near filter; the backend defaults to 100.
export const RADII = ['25', '50', '100', '250'];
export const DEFAULT_RADIUS = '100';

export interface BrowseFilters {
  q?: string;
  sport?: string;
  state?: string;
  month?: string; // YYYY-MM
  distance?: string;
  near?: string; // postcode
  within?: string; // km, one of RADII
  sort?: string; // 'distance' = closest first (only with near)
}

export interface BrowseResult {
  total: number;
  events: EventDetails[];
}

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// The next 12 months, for the month filter.
export const upcomingMonths = (now: Date = new Date()): { value: string; label: string }[] =>
  Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    return {
      value: `${d.getFullYear()}-${pad(d.getMonth() + 1)}`,
      label: d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }),
    };
  });

// Turns the page's filters into the API's query parameters.
export const filtersToParams = (f: BrowseFilters, limit: number, offset: number): Record<string, string | number> => {
  const params: Record<string, string | number> = { limit, offset };
  if (f.q) params.q = f.q;
  if (f.sport) params.sport = f.sport;
  if (f.state) params.state = f.state;
  if (f.distance) params.distance = f.distance;
  if (f.near) {
    params.near = f.near;
    if (f.within) params.within = f.within;
    if (f.sort === 'distance') params.sort = 'distance';
  }
  if (f.month) {
    const [y, m] = f.month.split('-').map(Number);
    params.from = `${y}-${pad(m)}-01`;
    params.to = ymd(new Date(y, m, 0)); // last day of the month
  }
  return params;
};

export const browseEvents = async (filters: BrowseFilters, limit = 24, offset = 0): Promise<BrowseResult> => {
  const response = await api.get<{ total: number; events: (Omit<EventDetails, 'EventDate'> & { EventDate: string })[] }>(
    '/events',
    { params: filtersToParams(filters, limit, offset) }
  );
  return {
    total: response.data.total,
    events: response.data.events.map((e) => ({ ...e, EventDate: new Date(e.EventDate) })),
  };
};

// The postcode saved on the signed-in user's account ('' if none).
export const fetchSavedPostcode = async (): Promise<string> => {
  const response = await api.get<UserDetails>('/getuserdetails');
  return (response.data?.Postcode ?? '').trim().toUpperCase();
};

// Whole days from today until the event (0 = today). Event dates are midnight UTC.
export const daysUntil = (eventDate: Date, now: Date = new Date()): number => {
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((eventDate.getTime() - today) / 86400000);
};

export const countdownLabel = (eventDate: Date, now: Date = new Date()): string => {
  const days = daysUntil(eventDate, now);
  if (days < 0) return 'Finished';
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days < 14) return `${days} days to go`;
  return `${Math.floor(days / 7)} weeks to go`;
};

export const formatEventDate = (date: Date) =>
  date.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

// Text in a calendar file needs commas, semicolons and backslashes escaped.
export const icsText = (s: string) => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

// An all-day calendar event (.ics) that Google, Apple and Outlook calendars import.
export const buildIcs = (event: EventDetails, pageUrl: string, now: Date = new Date()): string => {
  const day = event.EventDate.toISOString().slice(0, 10).replace(/-/g, '');
  const next = new Date(event.EventDate.getTime() + 86400000).toISOString().slice(0, 10).replace(/-/g, '');
  const stamp = now.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const location = [event.Street, event.City, event.County, event.PostCode].filter((p) => p && p.trim()).join(', ');
  const description = [event.Website ? `Event website: ${event.Website}` : '', `More on Sport Sphere: ${pageUrl}`]
    .filter(Boolean)
    .join('\n');
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Sport Sphere//Events//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:event-${event.Id}@sport-sphere.co.uk`,
    `DTSTAMP:${stamp}`,
    `DTSTART;VALUE=DATE:${day}`,
    `DTEND;VALUE=DATE:${next}`,
    `SUMMARY:${icsText(event.Name)}`,
    `LOCATION:${icsText(location)}`,
    `DESCRIPTION:${icsText(description)}`,
    `URL:${pageUrl}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
};

// Saves text as a file in the browser.
export const downloadText = (text: string, filename: string, type: string) => {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const downloadIcs = (event: EventDetails, pageUrl: string) =>
  downloadText(
    buildIcs(event, pageUrl),
    `${event.Name.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'event'}.ics`,
    'text/calendar;charset=utf-8'
  );

// Uses the phone's share sheet when available, otherwise copies the link.
export const shareLink = async (title: string, url: string): Promise<'shared' | 'copied' | 'failed'> => {
  try {
    if (navigator.share) {
      await navigator.share({ title, url });
      return 'shared';
    }
    await navigator.clipboard.writeText(url);
    return 'copied';
  } catch (err) {
    // Closing the share sheet counts as an AbortError: nothing to report.
    return err instanceof DOMException && err.name === 'AbortError' ? 'shared' : 'failed';
  }
};
